import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Search, Users, Briefcase, Receipt, UserCog } from "lucide-react";

const GROUPS = [
  ["leads", "Leads", Users, (x) => `${x.customer_name} · ${x.phone}`, (x) => x.company],
  ["jobs", "Jobs", Briefcase, (x) => `${x.job_no} · ${x.customer_name}`, (x) => x.status],
  ["bills", "Invoices", Receipt, (x) => `${x.invoice_no} · ${x.customer_name}`, (x) => x.payment_status],
  ["users", "Team", UserCog, (x) => x.name, (x) => x.role],
];
const DEST = { leads: "/admin/leads", jobs: "/admin/jobs", bills: "/admin/billing", users: "/admin/users" };

export default function CommandSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    const onKey = (e) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen(true); } };
    const onEvt = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("open-search", onEvt);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("open-search", onEvt); };
  }, []);

  useEffect(() => {
    if (!q || q.length < 2) { setRes(null); return; }
    const t = setTimeout(() => api.get("/search", { params: { q } }).then((r) => setRes(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = useCallback((group) => { setOpen(false); setQ(""); nav(DEST[group]); }, [nav]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="p-0 overflow-hidden max-w-lg" data-testid="command-search">
        <div className="flex items-center gap-3 px-4 border-b">
          <Search size={17} className="text-muted-foreground" />
          <input autoFocus data-testid="command-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search leads, jobs, invoices, people…"
            className="flex-1 py-4 bg-transparent outline-none text-sm" />
        </div>
        <div className="max-h-[50vh] overflow-y-auto p-2">
          {res && GROUPS.map(([key, label, Icon, line, tag]) => res[key]?.length > 0 && (
            <div key={key} className="mb-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-3 py-1.5">{label}</div>
              {res[key].map((x) => (
                <button key={x.id} data-testid={`search-result-${key}-${x.id}`} onClick={() => go(key)}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent text-sm text-left transition-colors">
                  <Icon size={15} className="text-muted-foreground shrink-0" />
                  <span className="flex-1 truncate">{line(x)}</span>
                  <span className="text-[11px] text-muted-foreground">{tag(x)}</span>
                </button>
              ))}
            </div>
          ))}
          {res && !GROUPS.some(([k]) => res[k]?.length) && (
            <div className="text-sm text-muted-foreground text-center py-8">No matches for “{q}”</div>
          )}
          {!res && <div className="text-xs text-muted-foreground text-center py-8">Type at least 2 characters — phone, name, job no, invoice no</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
